// src/pages/Exams/generate/ReviewStep.tsx
import { useEffect, useMemo, useState } from 'react';
import { QuestionCard } from './QuestionCard';
import { CompareFormsModal } from './CompareFormsModal';
import type { JobRow, QuestionDraft, QuestionFlag, RegenVariant } from './types';
import { Badge, Button, EmptyState, Segmented, ShowMoreFooter } from '../../../school_panel/components/ui';
import { Search } from 'lucide-react';

// 'all' | 'flagged' | 'low_quality' | a concrete flag name.
type FlagFilter = string;
type DifficultyFilter = 'all' | 'easy' | 'medium' | 'hard';

const DIFFICULTIES: Array<{ value: DifficultyFilter; label: string }> = [
  { value: 'all', label: 'Any' },
  { value: 'easy', label: 'Easy' },
  { value: 'medium', label: 'Medium' },
  { value: 'hard', label: 'Hard' },
];

const withCount = (label: string, n: number) => (
  <>{label} <span className="opacity-70">{n}</span></>
);

/** Question cards shown before the list collapses behind "Show all". */
const PREVIEW_LIMIT = 10;

const REGEN_VARIANTS: Array<{ value: RegenVariant; label: string }> = [
  { value: 'rephrase', label: 'Rephrase' },
  { value: 'harder', label: 'Make harder' },
  { value: 'easier', label: 'Make easier' },
  { value: 'regenerate', label: 'Fresh question' },
];

interface Props {
  job: JobRow;
  questions: QuestionDraft[];
  previewMode: boolean;
  onTogglePreview: (v: boolean) => void;
  onUpdate: (index: number, patch: Partial<QuestionDraft>) => void;
  onRemove: (index: number) => void;
  onRegenerate: (index: number, variant: RegenVariant) => Promise<unknown>;
  canUndoRemove: boolean;
  onUndoRemove: () => void;
  activeChapterFilter?: number | null;
  chapterNames?: Record<number, string>;
  onSetChapterFilter?: (id: number | null) => void;
  onToggleFlag?: (index: number, flag: QuestionFlag) => void;
  onBulkRegenerate?: (indices: number[], variant: RegenVariant) => Promise<void>;
  isBulkRegenerating?: boolean;
  lastRegen?: { index: number; prev: QuestionDraft } | null;
  onUndoRegenerate?: () => void;
  onClearLastRegen?: () => void;
}

export function ReviewStep({
  job, questions, previewMode, onTogglePreview, onUpdate, onRemove,
  onRegenerate, canUndoRemove, onUndoRemove,
  activeChapterFilter, chapterNames, onSetChapterFilter,
  onToggleFlag, onBulkRegenerate, isBulkRegenerating, lastRegen, onUndoRegenerate,
  onClearLastRegen,
}: Props) {
  const [form, setForm] = useState<string>('all');
  const [flagFilter, setFlagFilter] = useState<FlagFilter>('all');
  const [difficulty, setDifficulty] = useState<DifficultyFilter>('all');
  const [search, setSearch] = useState('');
  const [bulkVariant, setBulkVariant] = useState<RegenVariant>('rephrase');
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [compareOpen, setCompareOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);

  // Back to the 10-card preview whenever the filters change.
  useEffect(() => { setShowAll(false); }, [form, flagFilter, difficulty, search, activeChapterFilter]);

  // 8-second auto-dismiss for the regen-undo chip (spec §7.2).
  useEffect(() => {
    if (!lastRegen) return;
    const t = setTimeout(() => onClearLastRegen?.(), 8000);
    return () => clearTimeout(t);
  }, [lastRegen, onClearLastRegen]);

  // Selection is keyed by draft-array index; a remove/undo re-indexes the
  // array, so drop the selection whenever the question count changes to
  // avoid stale indices pointing at the wrong card.
  useEffect(() => { setSelected(new Set()); }, [questions.length]);

  const formLabels = useMemo(() => {
    const s = new Set(questions.map(q => q.form_label || 'A'));
    return Array.from(s).sort();
  }, [questions]);

  // Every flag present in the draft, so the one select covers "each flag"
  // (the coverage panel's old per-flag links).
  const flagNames = useMemo(() => {
    const s = new Set<string>();
    questions.forEach(q => (q.flags ?? []).forEach(f => s.add(f)));
    return Array.from(s).sort();
  }, [questions]);

  // A flag can disappear from the draft (last flagged question removed, or the
  // flag toggled off), which would otherwise leave the select on a value that
  // filters everything out.
  useEffect(() => {
    if (flagFilter === 'all' || flagFilter === 'flagged' || flagFilter === 'low_quality') return;
    if (!flagNames.includes(flagFilter)) setFlagFilter('all');
  }, [flagNames, flagFilter]);

  // Build {question, originalIndex} so callbacks address the draft's true index.
  const indexed = useMemo(
    () => questions.map((q, i) => ({ q, i })), [questions]);

  const visible = indexed.filter(({ q }) => {
    if (activeChapterFilter != null && q.chapter_id !== activeChapterFilter) return false;
    if (form !== 'all' && (q.form_label || 'A') !== form) return false;
    if (flagFilter === 'flagged' && (q.flags ?? []).length === 0) return false;
    if (flagFilter === 'low_quality' && (q.quality_score ?? 100) >= 50) return false;
    if (flagFilter !== 'all' && flagFilter !== 'flagged' && flagFilter !== 'low_quality'
      && !(q.flags ?? []).includes(flagFilter)) return false;
    if (difficulty !== 'all' && (q.difficulty || 'medium') !== difficulty) return false;
    if (search.trim()) {
      const s = search.trim().toLowerCase();
      const hay = [q.question, q.choice_a, q.choice_b, q.choice_c, q.choice_d]
        .join(' ').toLowerCase();
      if (!hay.includes(s)) return false;
    }
    return true;
  });

  const flaggedCount = questions.filter(q => (q.flags ?? []).length > 0).length;
  const lowCount = questions.filter(q => (q.quality_score ?? 100) < 50).length;

  const toggleSelect = (i: number) =>
    setSelected(prev => {
      const next = new Set(prev);
      next.has(i) ? next.delete(i) : next.add(i);
      return next;
    });

  const bulkRemove = () => {
    // remove from highest index first so earlier indices stay valid
    Array.from(selected).sort((a, b) => b - a).forEach(i => onRemove(i));
    setSelected(new Set());
  };

  return (
    <section aria-labelledby="rev-title" className="space-y-4">
      <div className="mt-[26px]">
        <h2 id="rev-title" className="font-display text-[22px] font-bold tracking-[-0.01em] text-ink">Review questions</h2>
        <p className="mt-1.5 flex flex-wrap gap-2">
          <Badge tone="info">{questions.length} question{questions.length === 1 ? '' : 's'}</Badge>
          <Badge tone={flaggedCount > 0 ? 'warning' : 'neutral'}>{flaggedCount} flagged</Badge>
          <Badge tone={lowCount > 0 ? 'danger' : 'neutral'}>{lowCount} low quality</Badge>
        </p>
      </div>
      <div className="space-y-3">
        <div className="flex flex-wrap items-end justify-between gap-3">
          {!previewMode ? (
            <div className="flex min-w-0 flex-[1_1_18rem] flex-wrap items-center gap-2.5">
              <Segmented<string>
                label="Show" scroll
                options={[
                  { key: 'all', label: withCount('All', questions.length) },
                  { key: 'flagged', label: withCount('Flagged', flaggedCount) },
                  { key: 'low_quality', label: withCount('Low quality', lowCount) },
                  ...flagNames.map(f => ({
                    key: f,
                    label: withCount(f, questions.filter(q => (q.flags ?? []).includes(f as QuestionFlag)).length),
                  })),
                ]}
                value={flagFilter}
                onChange={setFlagFilter} />
              <Segmented<DifficultyFilter>
                label="Difficulty"
                options={DIFFICULTIES.map(d => ({ key: d.value, label: d.label }))}
                value={difficulty}
                onChange={setDifficulty} />
              {chapterNames && onSetChapterFilter && (
                <label htmlFor="review-chapter"
                  className="flex min-h-[46px] min-w-0 items-center gap-2 rounded-[12px] border border-line-strong bg-surface px-3.5 focus-within:ring-2 focus-within:ring-accent">
                  <span className="text-[13px] text-muted">Chapter</span>
                  <select
                    id="review-chapter"
                    className="min-w-0 max-w-[16rem] border-0 bg-transparent text-sm text-ink focus:outline-none"
                    value={activeChapterFilter ?? ''}
                    onChange={(e) => onSetChapterFilter(e.target.value ? Number(e.target.value) : null)}>
                    <option value="">All chapters</option>
                    {Object.entries(chapterNames).map(([id, name]) => (
                      <option key={id} value={id}>{name}</option>
                    ))}
                  </select>
                </label>
              )}
              <label htmlFor="review-search"
                className="flex min-h-[46px] min-w-[200px] flex-[1_1_200px] items-center gap-2 rounded-[12px] border border-line-strong bg-surface px-3.5 focus-within:ring-2 focus-within:ring-accent">
                <Search size={18} strokeWidth={1.8} aria-hidden className="shrink-0 text-muted" />
                <span className="sr-only">Search</span>
                <input
                  id="review-search" type="search" placeholder="Search questions…"
                  className="min-w-0 flex-1 border-0 bg-transparent text-sm text-ink placeholder:text-muted focus:outline-none"
                  value={search} onChange={(e) => setSearch(e.target.value)} />
              </label>
            </div>
          ) : <div />}
          <div className="flex items-center gap-3 text-sm">
            <span className="font-mono text-muted">{visible.length} shown / {questions.length}</span>
            <label className="flex min-h-[44px] items-center gap-2 text-ink">
              <input type="checkbox" checked={previewMode} className="h-4 w-4 accent-accent"
                onChange={(e) => onTogglePreview(e.target.checked)} />
              Student preview
            </label>
          </div>
        </div>

        {!previewMode && formLabels.length > 1 && (
          <div className="flex flex-wrap items-center gap-2">
            <div role="tablist" className="flex flex-wrap items-center gap-1">
              {['all', ...formLabels].map(l => (
                <button key={l} type="button" role="tab" aria-selected={form === l}
                  onClick={() => setForm(l)}
                  className={`min-h-[44px] rounded-full border px-4 text-sm font-semibold transition-colors ${
                    form === l
                      ? 'border-navy bg-navy text-white dark:border-mist-fixed dark:bg-mist-fixed dark:text-navy'
                      : 'border-line-strong bg-surface text-ink hover:bg-surface-2'}`}>
                  {l === 'all' ? 'All forms' : `Form ${l}`}
                </button>
              ))}
            </div>
            <Button variant="ghost" size="sm" onClick={() => setCompareOpen(true)}>Compare forms</Button>
          </div>
        )}

        {!previewMode && (lastRegen || canUndoRemove) && !isBulkRegenerating && (
          <div className="flex flex-wrap items-center gap-2">
            {lastRegen && (
              <Button variant="ghost" size="sm" onClick={onUndoRegenerate}>Undo regenerate</Button>
            )}
            {canUndoRemove && (
              <Button variant="ghost" size="sm" onClick={onUndoRemove}>Undo remove</Button>
            )}
          </div>
        )}

        {!previewMode && selected.size > 0 && (
          <div className="flex flex-wrap items-center gap-2 rounded-[12px] border border-accent bg-accent-bg p-2.5 text-sm text-ink">
            <Badge tone="primary">{selected.size} selected</Badge>
            <Button variant="ghost" size="sm" disabled={isBulkRegenerating} onClick={bulkRemove}>
              Remove selected
            </Button>
            {onBulkRegenerate && (
              <>
                <div role="group" aria-label="Bulk regenerate variant" className="flex flex-wrap gap-1.5">
                  {REGEN_VARIANTS.map(v => (
                    <button key={v.value} type="button" aria-pressed={bulkVariant === v.value}
                      onClick={() => setBulkVariant(v.value)}
                      className={`inline-flex min-h-[34px] items-center rounded-[10px] border px-[11px] text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
                        bulkVariant === v.value
                          ? 'border-navy bg-navy text-white dark:border-mist-fixed dark:bg-mist-fixed dark:text-navy'
                          : 'border-line-strong bg-surface text-ink hover:border-slate'}`}>
                      {v.label}
                    </button>
                  ))}
                </div>
                <Button size="sm" disabled={isBulkRegenerating}
                  onClick={() => onBulkRegenerate(Array.from(selected), bulkVariant)
                    .then(() => setSelected(new Set()))
                    .catch((e) => { console.error('[exam_gen] bulk regenerate failed', e); })}>
                  {isBulkRegenerating ? 'Regenerating…' : 'Regenerate selected'}
                </Button>
              </>
            )}
            <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}>Clear</Button>
          </div>
        )}

        {job.phase === 'saved' && <p className="text-sm font-semibold text-success">Saved.</p>}
      </div>

      {visible.length === 0 ? (
        <EmptyState
          icon={<Search size={18} strokeWidth={1.8} />}
          title="No questions match this filter."
          body="Try another filter above."
        />
      ) : (
        (showAll ? visible : visible.slice(0, PREVIEW_LIMIT)).map(({ q, i }) => (
          <QuestionCard
            key={i} question={q} index={i}
            chapterName={chapterNames?.[q.chapter_id]}
            previewMode={previewMode}
            selected={selected.has(i)}
            highlight={!isBulkRegenerating && lastRegen?.index === i}
            onToggleSelect={toggleSelect}
            onUpdate={onUpdate}
            onRemove={isBulkRegenerating ? undefined : onRemove}
            onRegenerate={isBulkRegenerating ? undefined : onRegenerate}
            onToggleFlag={onToggleFlag}
          />
        ))
      )}

      {visible.length > PREVIEW_LIMIT && (
        <ShowMoreFooter
          shown={showAll ? visible.length : PREVIEW_LIMIT}
          total={visible.length}
          expanded={showAll}
          onToggle={() => setShowAll(v => !v)} />
      )}

      <CompareFormsModal open={compareOpen} onClose={() => setCompareOpen(false)}
        questions={questions} chapterNames={chapterNames} />
    </section>
  );
}
